import type { AllowedProducts, CountryData } from '@1trading/fast-api-domain';
import type { CountryList } from '@1trading/fast-api-domain/forms';
import { bopApi } from '@/lib/api/client';
import { toCsv } from '@/utils/csv';

export const PRODUCT_TYPES = ['SPOT', 'EQUITY_FUTURE', 'DATED_FUTURE'] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

// Option orders below are the legacy select orders — kept deliberately
export const AML_RISK_OPTIONS = ['high', 'low', 'medium', 'prohibited'] as const;
export type AmlRisk = (typeof AML_RISK_OPTIONS)[number];

export const POSTAL_CODE_USE_OPTIONS = ['none', 'optional', 'required'] as const;
export type PostalCodeUse = (typeof POSTAL_CODE_USE_OPTIONS)[number];

export const REQUIRED_DOCUMENT_ID_OPTIONS = ['tax'] as const;
export type JurisdictionDocumentId = (typeof REQUIRED_DOCUMENT_ID_OPTIONS)[number];

/** Raw per-country record from GET /v1/system/jurisdictions (object keyed by alpha-2 code) */
export type RawJurisdictionData = Omit<CountryData, 'phoneCode'> & {
  /** Array on pristine records; degrades to a plain string once edited through legacy BOP */
  phoneCode: string[] | string;
};

export type JurisdictionsResponse = Record<string, RawJurisdictionData>;

/** Editable (form) subset of the flattened row — everything except the constructed flagUrl */
export interface JurisdictionFormValues {
  code: string;
  name: string;
  alpha3: string;
  phoneCode: string;
  name_de: string;
  amlRisk: AmlRisk;
  postalCodeUse: PostalCodeUse;
  requiredDocumentIds: JurisdictionDocumentId[];
  allowedProductsRetail: ProductType[];
  allowedProductsProfessional: ProductType[];
  allowedResidency: boolean;
  allowedCitizenship: boolean;
  allowedFiatTransfers: boolean;
  eea: boolean;
  eu: boolean;
}

/** Flattened UI row (legacy Nuxt BFF reshape, reproduced client-side) */
export interface Jurisdiction extends JurisdictionFormValues {
  flagUrl: string;
}

const FLAG_CDN_BASE_URL = 'https://flagcdn.com';

/**
 * Object → sorted array flatten (legacy jurisdictions.get.ts).
 * - noFiatTransfers is negative-sense on the wire; the UI field is the positive
 *   allowedFiatTransfers — the inversion here pairs with the server re-inverting
 *   on write. Getting it wrong silently flips fiat permissions.
 * - phoneCode arrays collapse to their first element (legacy data-loss bug kept
 *   for wire compatibility; already-string values pass through).
 * - postalCodeUse defaults to 'required' for unseeded records at read time so the
 *   required select isn't empty.
 */
export const flattenJurisdictions = (data: JurisdictionsResponse): Jurisdiction[] =>
  Object.entries(data)
    .map(([code, entry]) => ({
      code,
      name: entry.name,
      alpha3: entry.alpha3,
      phoneCode: Array.isArray(entry.phoneCode) ? entry.phoneCode[0] : entry.phoneCode,
      allowedResidency: entry.allowedResidency,
      allowedCitizenship: entry.allowedCitizenship,
      allowedFiatTransfers: !entry.noFiatTransfers,
      eea: entry.eea,
      eu: entry.eu,
      name_de: entry.name_de,
      amlRisk: entry.amlRisk,
      flagUrl: `${FLAG_CDN_BASE_URL}/${code.toLowerCase()}.svg`,
      requiredDocumentIds: entry.requiredDocumentIds || [],
      postalCodeUse: entry.postalCodeUse || 'required',
      allowedProductsRetail: entry.allowedProducts?.retail || [],
      allowedProductsProfessional: entry.allowedProducts?.professional || [],
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

/** Reads the raw response because the flattened rows invert noFiatTransfers and keep one phone code; a lone code arrives as a string */
export const toCountryList = (data: JurisdictionsResponse): CountryList =>
  Object.fromEntries(Object.entries(data).map(([code, entry]) => [code, { ...entry, phoneCode: Array.isArray(entry.phoneCode) ? entry.phoneCode : [entry.phoneCode] }]));

/** POST body for /add and /edit (identical shape) */
export interface SaveJurisdictionRequest {
  allowedCitizenship: boolean;
  /** POSITIVE sense — the server stores noFiatTransfers = !allowedFiatTransfers */
  allowedFiatTransfers: boolean;
  allowedResidency: boolean;
  alpha3: string;
  amlRisk: string;
  code: string;
  eea: boolean;
  eu: boolean;
  name: string;
  name_de: string;
  phoneCode: string;
  /** Audit justification — required by the server */
  comment: string;
  requiredDocumentIds: string[];
  postalCodeUse: PostalCodeUse;
  allowedProducts: AllowedProducts;
}

/**
 * Form → wire repack (legacy jurisdictions.post.ts / jurisdictions-update.ts).
 * allowedProducts must always carry BOTH keys — the server validates the object has
 * exactly `retail` and `professional`. Booleans arrive zod-validated, so
 * the legacy `!!` checkbox coercion is unnecessary here.
 */
export const repackJurisdiction = (values: JurisdictionFormValues, comment: string): SaveJurisdictionRequest => ({
  allowedCitizenship: values.allowedCitizenship,
  allowedFiatTransfers: values.allowedFiatTransfers,
  allowedResidency: values.allowedResidency,
  alpha3: values.alpha3,
  amlRisk: values.amlRisk,
  code: values.code,
  eea: values.eea,
  eu: values.eu,
  name: values.name,
  name_de: values.name_de,
  phoneCode: values.phoneCode,
  comment,
  requiredDocumentIds: values.requiredDocumentIds,
  postalCodeUse: values.postalCodeUse,
  allowedProducts: {
    retail: values.allowedProductsRetail || [],
    professional: values.allowedProductsProfessional || [],
  },
});

/** CSV column order = every key of the flattened row */
export const JURISDICTION_CSV_COLUMNS: ReadonlyArray<keyof Jurisdiction> = [
  'code',
  'name',
  'alpha3',
  'phoneCode',
  'allowedResidency',
  'allowedCitizenship',
  'allowedFiatTransfers',
  'eea',
  'eu',
  'name_de',
  'amlRisk',
  'flagUrl',
  'requiredDocumentIds',
  'postalCodeUse',
  'allowedProductsRetail',
  'allowedProductsProfessional',
];

// Raw values only — booleans as true/false, arrays comma-joined. The formatted ✅/❌
// cell rendering must never leak into the export.
export const buildJurisdictionsCsv = (rows: Jurisdiction[]): string =>
  toCsv(
    rows.map((row): Record<string, unknown> => ({ ...row })),
    [...JURISDICTION_CSV_COLUMNS],
  );

export const getJurisdictions = () => bopApi.get('v1/system/jurisdictions').json<JurisdictionsResponse>();

// Success responses are plain ok bodies — .text(), never .json()
export const addJurisdiction = (body: SaveJurisdictionRequest) => bopApi.post('v1/system/jurisdictions/add', { json: body }).text();

export const editJurisdiction = (body: SaveJurisdictionRequest) => bopApi.post('v1/system/jurisdictions/edit', { json: body }).text();

// Only { code, comment } reach bop-api on delete
export const deleteJurisdiction = (body: { code: string; comment: string }) => bopApi.post('v1/system/jurisdictions/delete', { json: body }).text();
